import { useEffect, useState } from 'react';
import { Button, Input, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, Modal, EmptyState } from '../../../../shared/components';
import { PlusIcon, EditIcon, TrashIcon } from '../../../../shared/icons';
import { getResultsRepository } from '../../../../shared/services/results/repositoryFactory';
import { ServiceType } from '../../../../shared/services/results/types';

const repo = getResultsRepository();

/** Parámetros > Tipo de Servicio: catálogo usado como filtro en "Tabla Resumen". */
export function ServiceTypeCatalogTab() {
  const toast = useToast();
  const [rows, setRows] = useState<ServiceType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ServiceType | null>(null);
  const [nombre, setNombre] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState<ServiceType | null>(null);

  const load = () => {
    setIsLoading(true);
    repo.listServiceTypes().then((r) => {
      setRows(r);
      setIsLoading(false);
    });
  };
  useEffect(load, []);

  const openCreate = () => {
    setNombre('');
    setCreating(true);
  };
  const openEdit = (r: ServiceType) => {
    setNombre(r.nombre);
    setEditing(r);
  };
  const close = () => {
    setCreating(false);
    setEditing(null);
  };
  const save = async () => {
    if (!nombre.trim()) {
      toast.show('El nombre es obligatorio.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      if (editing) {
        await repo.updateServiceType(editing.id, { nombre: nombre.toUpperCase() });
        toast.show('Tipo de servicio actualizado.');
      } else {
        await repo.createServiceType({ nombre: nombre.toUpperCase(), isActive: true });
        toast.show('Tipo de servicio agregado.');
      }
      close();
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const columns: Column<ServiceType>[] = [
    { key: 'nombre', header: 'Nombre', render: (r) => r.nombre, sortable: true, sortValue: (r) => r.nombre },
    { key: 'activo', header: 'Estado', render: (r) => <Badge tone={r.isActive ? 'success' : 'neutral'}>{r.isActive ? 'Activo' : 'Inactivo'}</Badge> },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="flex gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar" onClick={() => openEdit(r)} className="h-9 w-9 text-ink-500 hover:bg-ink-100" />
          <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => setDeleting(r)} className="h-9 w-9 text-critical hover:bg-red-50" />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Button leftIcon={<PlusIcon width={18} height={18} />} onClick={openCreate}>
        Agregar tipo de servicio
      </Button>

      {rows.length === 0 && !isLoading ? (
        <EmptyState icon="🏷️" title="Sin tipos de servicio" />
      ) : (
        <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} isLoading={isLoading} pageSize={10} pageSizeOptions={[10, 25, 50]} emptyTitle="Sin tipos de servicio" />
      )}

      <Modal open={creating || !!editing} onClose={close} title={editing ? 'Editar tipo de servicio' : 'Agregar tipo de servicio'}>
        <div className="space-y-3">
          <Input label="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} />
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={close}>Cancelar</Button>
            <Button fullWidth onClick={save} isLoading={isSaving}>Guardar</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title="Eliminar tipo de servicio"
        description={`¿Eliminar "${deleting?.nombre}"?`}
        tone="danger"
        confirmLabel="Eliminar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await repo.deleteServiceType(deleting.id);
          setDeleting(null);
          load();
        }}
      />
    </div>
  );
}
